import { useMemo } from 'react';
import clsx from 'clsx';
import { useBrainStore } from '@/state/brainStore';
import { deriveCustomerFlags, pickLatestSignedWaiver } from '@/domain/customerFlags';
import { CUSTOMER_FLAG_STYLE } from '@/utils/customerFlagStyle';
import type { CustomerRecord, Waiver } from '@/domain/types';

interface Props {
  customer: CustomerRecord;
  className?: string;
}

/**
 * Derived customer-level flags (waiver-signed / medical-condition / allergy /
 * under-medication), rendered as chips. Reactive to the waiver store so a
 * freshly-signed waiver lights up the flags immediately. Renders nothing when
 * there are no flags.
 */
export default function CustomerFlagChips({ customer, className }: Props) {
  const waivers = useBrainStore((s) => s.waivers);

  const flags = useMemo(() => {
    const linked = (customer.waivers ?? [])
      .map((id) => waivers[id])
      .filter((w): w is Waiver => Boolean(w));
    const forCustomer = linked.length
      ? linked
      : Object.values(waivers).filter((w) => w.customerId === customer.id);
    return deriveCustomerFlags(customer, pickLatestSignedWaiver(forCustomer));
  }, [customer, waivers]);

  if (!flags.length) return null;

  return (
    <div className={clsx('flex flex-wrap gap-1', className)} data-testid="customer-flags">
      {flags.map((flag) => (
        <span
          key={flag}
          className={clsx(
            'inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium',
            CUSTOMER_FLAG_STYLE[flag].cls,
          )}
          data-testid={`customer-flag-${flag}`}
        >
          {CUSTOMER_FLAG_STYLE[flag].label}
        </span>
      ))}
    </div>
  );
}
